/**
 * Template Service
 * Handles workout template operations for both IndexedDB and Supabase
 *
 * This service provides:
 * - Local storage (IndexedDB) operations for offline-first functionality
 * - Cloud sync (Supabase) operations for data persistence
 * - Utility functions for template-workout conversions
 * - Usage tracking and statistics
 *
 * @module services/templates
 */

import { getDB, addToSyncQueue } from './db.js'
import { supabase, getCurrentUser } from './supabase.js'

// ============================================
// IndexedDB Operations
// ============================================

/**
 * Save template to IndexedDB
 * Generates UUID if template.id is not provided
 * Automatically adds to sync queue for cloud sync
 *
 * @param {Object} template - Template object to save
 * @param {string} [template.id] - Template UUID (auto-generated if not provided)
 * @param {string} template.name - Template name
 * @param {string} [template.description] - Template description
 * @param {number} [template.use_count=0] - Usage count
 * @param {string} [template.last_used_at] - Last used timestamp (ISO)
 * @returns {Promise<Object>} Saved template object
 */
export async function saveTemplate(template) {
  const db = getDB()

  // Generate ID if not exists
  if (!template.id) {
    template.id = crypto.randomUUID()
  }

  template.synced = false
  template.updated_at = new Date().toISOString()

  if (!template.created_at) {
    template.created_at = template.updated_at
  }

  await db.put('workoutTemplates', template)

  // Add to sync queue
  await addToSyncQueue({
    action: 'saveTemplate',
    data: template,
    timestamp: Date.now()
  })

  return template
}

/**
 * Get all templates from IndexedDB with optional filtering
 *
 * @param {Object} [filters={}] - Filter options
 * @param {string} [filters.search] - Search keyword (matches name or description)
 * @returns {Promise<Array>} Array of templates, sorted by last_used_at (desc) then name
 */
export async function getTemplates(filters = {}) {
  const db = getDB()
  let templates = await db.getAll('workoutTemplates')

  // Apply filters
  if (filters.search) {
    const searchLower = filters.search.toLowerCase()
    templates = templates.filter(t =>
      t.name.toLowerCase().includes(searchLower) ||
      (t.description && t.description.toLowerCase().includes(searchLower))
    )
  }

  // Sort by last used date (most recent first), then by name
  templates.sort((a, b) => {
    if (a.last_used_at && b.last_used_at) {
      return new Date(b.last_used_at) - new Date(a.last_used_at)
    }
    if (a.last_used_at) return -1
    if (b.last_used_at) return 1
    return a.name.localeCompare(b.name, 'zh-TW')
  })

  return templates
}

/**
 * Get a single template by ID from IndexedDB
 */
export async function getTemplate(id) {
  const db = getDB()
  return await db.get('workoutTemplates', id)
}

/**
 * Delete template from IndexedDB
 */
export async function deleteTemplate(id) {
  const db = getDB()

  await db.delete('workoutTemplates', id)

  // Delete associated template exercises
  const exercises = await db.getAllFromIndex('templateExercises', 'template_id', id)
  for (const exercise of exercises) {
    await db.delete('templateExercises', exercise.id)
  }

  // Add to sync queue
  await addToSyncQueue({
    action: 'deleteTemplate',
    data: { id },
    timestamp: Date.now()
  })
}

/**
 * Save template exercise to IndexedDB
 */
export async function saveTemplateExercise(exercise) {
  const db = getDB()

  // Generate ID if not exists
  if (!exercise.id) {
    exercise.id = crypto.randomUUID()
  }

  await db.put('templateExercises', exercise)
  return exercise
}

/**
 * Get template exercises by template ID from IndexedDB
 */
export async function getTemplateExercises(templateId) {
  const db = getDB()
  const exercises = await db.getAllFromIndex('templateExercises', 'template_id', templateId)

  // Sort by order_index
  exercises.sort((a, b) => a.order_index - b.order_index)

  return exercises
}

/**
 * Get template with exercises from IndexedDB
 */
export async function getTemplateWithExercises(templateId) {
  const template = await getTemplate(templateId)
  if (!template) return null

  const exercises = await getTemplateExercises(templateId)

  return { template, exercises }
}

/**
 * Save template with exercises (transaction) to IndexedDB
 */
export async function saveTemplateWithExercises(template, exercises = []) {
  const db = getDB()

  // Generate template ID if not exists
  if (!template.id) {
    template.id = crypto.randomUUID()
  }

  template.synced = false
  template.updated_at = new Date().toISOString()
  if (!template.created_at) {
    template.created_at = template.updated_at
  }

  // Save template
  await db.put('workoutTemplates', template)

  // Delete existing template exercises
  const existingExercises = await db.getAllFromIndex('templateExercises', 'template_id', template.id)
  for (const ex of existingExercises) {
    await db.delete('templateExercises', ex.id)
  }

  // Save new exercises
  for (let i = 0; i < exercises.length; i++) {
    const exercise = exercises[i]
    if (!exercise.id) {
      exercise.id = crypto.randomUUID()
    }
    exercise.template_id = template.id
    exercise.order_index = i
    await db.put('templateExercises', exercise)
  }

  // Add to sync queue
  await addToSyncQueue({
    action: 'saveTemplate',
    data: { template, exercises },
    timestamp: Date.now()
  })

  return { template, exercises }
}

/**
 * Create a template from an existing workout
 * Copies workout exercises to template exercises with completed flags reset
 *
 * @param {string} workoutId - Workout UUID to convert
 * @param {string} templateName - Name for the new template
 * @param {string} [templateDescription=''] - Optional description
 * @returns {Promise<Object>} Object containing { template, exercises }
 * @throws {Error} If workout not found
 */
export async function createTemplateFromWorkout(workoutId, templateName, templateDescription = '') {
  const db = getDB()

  // Get workout with exercises
  const workout = await db.get('workouts', workoutId)
  if (!workout) {
    throw new Error('Workout not found')
  }

  const workoutExercises = await db.getAllFromIndex('exercises', 'workout_id', workoutId)

  // Create template
  const template = {
    name: templateName,
    description: templateDescription,
    use_count: 0,
    last_used_at: null
  }

  // Convert workout exercises to template exercises
  const templateExercises = workoutExercises.map((ex, index) => ({
    exercise_name: ex.exercise_name,
    order_index: index,
    sets: Array.isArray(ex.sets)
      ? ex.sets.map(set => ({
          weight: set.weight || null,
          reps: set.reps || null,
          completed: false
        }))
      : [],
    rest_time: ex.rest_time || null,
    notes: ex.notes || ''
  }))

  return await saveTemplateWithExercises(template, templateExercises)
}

/**
 * Create a workout from a template
 * Converts template exercises to workout exercises with fresh UUIDs
 * Note: Workout is NOT saved automatically, caller must save it
 *
 * @param {string} templateId - Template UUID to use
 * @returns {Promise<Object>} Object containing { workout, exercises } ready to save
 * @throws {Error} If template not found
 */
export async function createWorkoutFromTemplate(templateId) {
  const db = getDB()

  // Get template with exercises
  const template = await getTemplate(templateId)
  if (!template) {
    throw new Error('Template not found')
  }

  const templateExercises = await getTemplateExercises(templateId)

  // Create workout data (not saved yet)
  const workout = {
    id: crypto.randomUUID(),
    date: new Date().toISOString(),
    type: 'strength',
    duration: null,
    notes: '',
    template_id: templateId
  }

  // Convert template exercises to workout exercises
  const exercises = templateExercises.map(ex => ({
    id: crypto.randomUUID(),
    exercise_name: ex.exercise_name,
    sets: Array.isArray(ex.sets)
      ? ex.sets.map(set => ({
          weight: set.weight || 0,
          reps: set.reps || 0,
          completed: true  // Mark all sets as completed when importing from template
        }))
      : [{ weight: 0, reps: 0, completed: true }],
    rest_time: ex.rest_time || null,
    notes: ex.notes || ''
  }))

  return { workout, exercises }
}

/**
 * Update template usage statistics
 * Increments use_count and updates last_used_at timestamp
 * Automatically adds to sync queue
 *
 * @param {string} templateId - Template UUID
 * @returns {Promise<Object|undefined>} Updated template object, or undefined if not found
 */
export async function updateTemplateUsage(templateId) {
  const db = getDB()

  const template = await getTemplate(templateId)
  if (!template) return

  template.use_count = (template.use_count || 0) + 1
  template.last_used_at = new Date().toISOString()
  template.updated_at = new Date().toISOString()

  await db.put('workoutTemplates', template)

  // Add to sync queue
  await addToSyncQueue({
    action: 'updateTemplateUsage',
    data: { id: templateId, use_count: template.use_count, last_used_at: template.last_used_at },
    timestamp: Date.now()
  })

  return template
}

// ============================================
// Supabase Operations
// ============================================

/**
 * Sync template to Supabase
 */
export async function syncTemplateToSupabase(template, exercises = []) {
  const user = await getCurrentUser()
  if (!user) {
    throw new Error('User not authenticated')
  }

  // Prepare template data
  const templateData = {
    id: template.id,
    user_id: user.id,
    name: template.name,
    description: template.description || null,
    created_at: template.created_at,
    updated_at: template.updated_at,
    last_used_at: template.last_used_at || null,
    use_count: template.use_count || 0
  }

  // Upsert template
  const { data: savedTemplate, error: templateError } = await supabase
    .from('workout_templates')
    .upsert(templateData)
    .select()
    .single()

  if (templateError) {
    throw new Error(`Failed to sync template: ${templateError.message}`)
  }

  // Delete existing template exercises in Supabase
  const { error: deleteError } = await supabase
    .from('template_exercises')
    .delete()
    .eq('template_id', template.id)

  if (deleteError) {
    console.error('Failed to delete old template exercises:', deleteError)
  }

  // Insert template exercises
  if (exercises.length > 0) {
    const exercisesData = exercises.map(ex => ({
      id: ex.id,
      template_id: template.id,
      exercise_name: ex.exercise_name,
      order_index: ex.order_index,
      sets: ex.sets,
      rest_time: ex.rest_time || null,
      notes: ex.notes || null
    }))

    const { error: exercisesError } = await supabase
      .from('template_exercises')
      .insert(exercisesData)

    if (exercisesError) {
      throw new Error(`Failed to sync template exercises: ${exercisesError.message}`)
    }
  }

  return savedTemplate
}

/**
 * Fetch templates from Supabase
 */
export async function fetchTemplatesFromSupabase() {
  const user = await getCurrentUser()
  if (!user) {
    throw new Error('User not authenticated')
  }

  const { data: templates, error: templatesError } = await supabase
    .from('workout_templates')
    .select('*')
    .eq('user_id', user.id)
    .order('last_used_at', { ascending: false, nullsFirst: false })

  if (templatesError) {
    throw new Error(`Failed to fetch templates: ${templatesError.message}`)
  }

  return templates || []
}

/**
 * Fetch template with exercises from Supabase
 */
export async function fetchTemplateWithExercisesFromSupabase(templateId) {
  const user = await getCurrentUser()
  if (!user) {
    throw new Error('User not authenticated')
  }

  // Fetch template
  const { data: template, error: templateError } = await supabase
    .from('workout_templates')
    .select('*')
    .eq('id', templateId)
    .eq('user_id', user.id)
    .single()

  if (templateError) {
    throw new Error(`Failed to fetch template: ${templateError.message}`)
  }

  // Fetch exercises
  const { data: exercises, error: exercisesError } = await supabase
    .from('template_exercises')
    .select('*')
    .eq('template_id', templateId)
    .order('order_index', { ascending: true })

  if (exercisesError) {
    throw new Error(`Failed to fetch template exercises: ${exercisesError.message}`)
  }

  return { template, exercises: exercises || [] }
}

/**
 * Delete template from Supabase
 */
export async function deleteTemplateFromSupabase(templateId) {
  const user = await getCurrentUser()
  if (!user) {
    throw new Error('User not authenticated')
  }

  const { error } = await supabase
    .from('workout_templates')
    .delete()
    .eq('id', templateId)
    .eq('user_id', user.id)

  if (error) {
    throw new Error(`Failed to delete template: ${error.message}`)
  }

  // Template exercises will be deleted automatically by CASCADE
}

/**
 * Update template usage in Supabase
 */
export async function updateTemplateUsageInSupabase(templateId, useCount, lastUsedAt) {
  const user = await getCurrentUser()
  if (!user) {
    throw new Error('User not authenticated')
  }

  const { error } = await supabase
    .from('workout_templates')
    .update({
      use_count: useCount,
      last_used_at: lastUsedAt,
      updated_at: new Date().toISOString()
    })
    .eq('id', templateId)
    .eq('user_id', user.id)

  if (error) {
    throw new Error(`Failed to update template usage: ${error.message}`)
  }
}
